import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack, Stepper, Step, StepLabel } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { FormRenderer } from '../components/formio';
import { useSubmitFormioSubmission } from '../hooks/useSubmitFormioSubmission';
import LoadingBackdrop from '../components/common/custom-component/LoadingBackdrop';

// Mock-only page standing in for the future pending-task ("Execute Task") and
// history ("View History") pages - both are the same "render a submission's
// payload via FormRenderer" behavior, differing only in read-only mode, so
// they share this one page rather than being duplicated. See ManageFormsPage.jsx's
// handleExecuteTask/handleViewHistory and WorkflowHistoryPageRemastered.jsx's
// handleView for how navigation here is triggered.
//
// `submissions` is always an array - Execute Task passes a single entry (no
// stepper shown), while View History across a process instance can span
// several independently-defined forms (different formId/schema each), shown
// as steps. This outer stepper only switches which submission/form is
// mounted; it's unrelated to Form.io's own internal wizard stepper, which a
// given form renders on its own if that form's schema happens to be a wizard.
const FormRendererPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { submissions = [], readOnly = false, title, activeStep: defaultStep } = location.state || {};
  const [formTitles, setFormTitles] = useState({});
  const [activeStep, setActiveStep] = useState(() => {
    if (typeof defaultStep === 'number' && defaultStep >= 0 && defaultStep < submissions.length) {
      return defaultStep;
    }
    // Default to the first non-readOnly step, or the last step if all are readOnly
    const editableIndex = submissions.findIndex((s) => !s.readOnly);
    return editableIndex !== -1 ? editableIndex : 0;
  });
  const { submitSubmission } = useSubmitFormioSubmission();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSchemasLoaded = useCallback((schemas) => {
    if (!Array.isArray(schemas)) return;
    const titlesMap = {};
    schemas.forEach((schema, index) => {
      if (schema?.title || schema?.name) {
        titlesMap[index] = schema.title || schema.name;
      }
    });
    setFormTitles(titlesMap);
  }, []);

  const currentSubmission = submissions[activeStep];
  const isCurrentReadOnly = readOnly || Boolean(currentSubmission?.readOnly);

  // Goes back to whatever page the user actually came from, rather than a
  // hardcoded route - matters once this is reached from multiple real places
  // (pending tasks, history, etc), not just ManageFormsPage's mock buttons.
  const handleBack = () => navigate(-1);

  // Reuses the same submit path as ManageFormsPage's Fill Form tab (same
  // thunk, same dirty-fields-only payload) via the shared hook - completing
  // a task patches the same submission it was loaded from.
  const handleSubmit = useCallback(
    async ({ changedFields }) => {
      if (!currentSubmission) return;
      setIsSubmitting(true);
      const taskId = currentSubmission.taskId || location.state?.taskId;
      const success = await submitSubmission({
        formId: currentSubmission.formId,
        submissionId: currentSubmission.submissionId,
        processInstanceId: currentSubmission.processInstanceId,
        taskId,
        changedFields,
      });
      setIsSubmitting(false);
      if (success) {
        navigate(-1);
      }
    },
    [submitSubmission, currentSubmission, navigate]
  );

  if (!currentSubmission) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          No submission selected
        </Typography>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back
        </Button>
        <Typography variant="h4">
          {title || (readOnly ? 'View History' : 'Execute Task')}
        </Typography>
      </Stack>

      {submissions.length > 1 && (
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {submissions.map((submission, index) => (
            <Step key={submission.submissionId || `${submission.formId}-${index}`}>
              <StepLabel sx={{ cursor: 'pointer' }} onClick={() => setActiveStep(index)}>
                {formTitles[index] || submission.label || submission.formId}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <FormRenderer
        submissions={submissions}
        activeIndex={activeStep}
        readOnly={isCurrentReadOnly}
        onSchemasLoaded={handleSchemasLoaded}
        onSubmit={handleSubmit}
      />

      <LoadingBackdrop open={isSubmitting} message="Submitting form, please wait..." variant="bars" color="greenGradient" />
    </Box>
  );
};

export default FormRendererPage;
